import { useState, useEffect } from 'react';
import { CaseStudyItem } from '@/types/case-study';
import { generateSlug, getDefaultCaseStudy, validateTitle, isTitleValid } from '@/utils/caseStudyUtils';

interface UseCaseStudyFormStateProps {
  initialData?: CaseStudyItem | Partial<CaseStudyItem>;
}

interface UseCaseStudyFormStateReturn {
  caseStudy: CaseStudyItem;
  setCaseStudy: React.Dispatch<React.SetStateAction<CaseStudyItem>>;
  activeSection: string | null;
  setActiveSection: (sectionId: string | null) => void;
  showSectionSelector: boolean;
  setShowSectionSelector: (show: boolean) => void;
  showPreviewModal: boolean;
  setShowPreviewModal: (show: boolean) => void;
  isDetailsCollapsed: boolean;
  setIsDetailsCollapsed: (collapsed: boolean) => void;
  titleError: string;
  setTitleError: (error: string) => void;
  isTitleValid: boolean;
  validateTitle: (title: string) => boolean;
}

export const useCaseStudyFormState = ({ 
  initialData 
}: UseCaseStudyFormStateProps): UseCaseStudyFormStateReturn => {
  
  const [caseStudy, setCaseStudy] = useState<CaseStudyItem>(() => {
    const defaults = getDefaultCaseStudy();
    
    if (initialData) {
      // Merge initialData with defaults to ensure all required fields are present
      const merged = {
        ...defaults,
        ...initialData,
        // Ensure sections array exists
        sections: initialData.sections || [],
        // Ensure required fields have defaults if not provided
        id: initialData.id || crypto.randomUUID(),
        created_at: initialData.created_at || new Date().toISOString(),
      };
      return merged;
    } else {
      // Create new case study with defaults
      return {
        ...defaults,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        sections: [],
      };
    }
  });

  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [showSectionSelector, setShowSectionSelector] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isDetailsCollapsed, setIsDetailsCollapsed] = useState(false);
  const [titleError, setTitleError] = useState<string>("");

  // Update case study when initialData changes (e.g., when data is loaded asynchronously)
  useEffect(() => {
    if (initialData && initialData.id && initialData.id !== caseStudy.id) {
      const defaults = getDefaultCaseStudy();
      const updated = {
        ...defaults,
        ...initialData,
        sections: initialData.sections || [],
        // Ensure required fields have defaults if not provided
        id: initialData.id || crypto.randomUUID(),
        created_at: initialData.created_at || new Date().toISOString(),
      };
      setCaseStudy(updated);
    }
  }, [initialData, caseStudy.id]);

  // Check if title is valid for enabling/disabling save buttons
  const titleValid = isTitleValid(caseStudy.title);

  // Validate title function
  const validateTitleFunction = (title: string): boolean => {
    const validation = validateTitle(title);
    if (!validation.isValid) {
      setTitleError(validation.error || 'Title is required and cannot be empty');
      return false;
    }
    setTitleError('');
    return true;
  };

  // Update document title when case study title or status changes
  useEffect(() => {
    const baseTitle = "Design Project Case Study";
    const statusIndicator = caseStudy.status === 'published' ? ' (Published)' : ' (Draft)';
    
    if (caseStudy.title && caseStudy.title.trim()) {
      document.title = `${caseStudy.title}${statusIndicator} - ${baseTitle}`;
    } else {
      document.title = `${baseTitle}${statusIndicator}`;
    }
  }, [caseStudy.title, caseStudy.status]);

  // Auto-generate slug when title changes
  useEffect(() => {
    if (caseStudy.title) {
      setCaseStudy(prev => ({
        ...prev,
        slug: generateSlug(prev.title)
      }));
    }
  }, [caseStudy.title]);

  // Auto-fill SEO and Open Graph fields when they're empty
  useEffect(() => {
    setCaseStudy(prev => {
      const updates: Partial<CaseStudyItem> = {};
      
      // Only auto-fill if the field is empty and we have source data
      if (!prev.seo_title && prev.title) {
        updates.seo_title = prev.title;
      }
      if (!prev.seo_description && prev.description) {
        updates.seo_description = prev.description;
      }
      if (!prev.og_image && prev.cover_image) {
        updates.og_image = prev.cover_image;
      }
      
      // Only update if there are changes to avoid infinite loops
      if (Object.keys(updates).length > 0) {
        return { ...prev, ...updates };
      }
      
      return prev;
    });
  }, [caseStudy.title, caseStudy.description, caseStudy.cover_image]);

  return {
    caseStudy,
    setCaseStudy,
    activeSection,
    setActiveSection,
    showSectionSelector,
    setShowSectionSelector,
    showPreviewModal,
    setShowPreviewModal,
    isDetailsCollapsed,
    setIsDetailsCollapsed,
    titleError,
    setTitleError,
    isTitleValid: titleValid,
    validateTitle: validateTitleFunction,
  };
};
